import { Injectable } from '@angular/core';
import {
  collection,
  collectionData,
  deleteDoc,
  doc,
  docData,
  Firestore,
  setDoc,
  Timestamp,
} from '@angular/fire/firestore';
import { Observable, map } from 'rxjs';
import { TourDetailInterface } from '../../core/models';

@Injectable({
  providedIn: 'root',
})
export class TourDetailService {
  private tourCollection: ReturnType<typeof collection>;

  constructor(private firestore: Firestore) {
    this.tourCollection = collection(this.firestore, 'tours');
  }

  /**
   * Create a new tour with a generated ID
   */
  createNewTour(tour: Omit<TourDetailInterface, 'id'>): Promise<void> {
    const newDocRef = doc(this.tourCollection);
    const id = newDocRef.id;

    const tourWithId: TourDetailInterface = {
      id,
      tourName: tour.tourName,

      createdDate: Timestamp.now(),
      lastEditDate: Timestamp.now(),
      colorCode: tour.colorCode ?? '',
    };

    return setDoc(newDocRef, tourWithId);
  }

  /**
   * Get all tours, sorted by language then info
   */
  getTours(): Observable<TourDetailInterface[]> {
    return collectionData(this.tourCollection, { idField: 'id' }).pipe(
      map((tours) => tours as TourDetailInterface[]),
      map((tours) =>
        tours.sort((a, b) => {
          const langCmp = a.tourName.localeCompare(b.tourName);
          return langCmp !== 0 ? langCmp : a.tourName.localeCompare(b.tourName);
        }),
      ),
    );
  }

  /**
   * Get a single tour by ID
   */
  getTourData(id: string): Observable<TourDetailInterface> {
    const tourDocRef = doc(this.firestore, 'tours', id);
    return docData(tourDocRef, {
      idField: 'id',
    }) as Observable<TourDetailInterface>;
  }

  /**
   * Update an existing tour
   */
  updateTour(tour: TourDetailInterface): Promise<void> {
    tour.lastEditDate = Timestamp.now();
    const tourDocRef = doc(this.firestore, `tours/${tour.id}`);
    return setDoc(tourDocRef, tour);
  }

  /**
   * Delete a tour
   */
  deleteTour(tourId: string): Promise<void> {
    const tourDocRef = doc(this.firestore, `tours/${tourId}`);
    return deleteDoc(tourDocRef);
  }
}
